"use strict";
// Contract payment-schedule projection (the ticket's `ticket_payment_schedule`
// lines projected onto a BL) and the pure prose formatters that render it.
// Framework-agnostic so both the BL read card and the invoicing dialogs share
// one source of truth.
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatPercent = formatPercent;
exports.labelFor = labelFor;
exports.formatTriggerPhrase = formatTriggerPhrase;
exports.buildContractTermsSummary = buildContractTermsSummary;
// `kind` → label stem. `BLTranche` gets a 1-based running index appended (see
// `labelFor`); unknown kinds fall back to the raw kind string and never throw.
const KIND_LABEL = {
    Downpayment: 'Advance Downpayment',
    BLTranche: 'BL Tranche',
};
// The wire sends `percent` as a decimal string (e.g. "0.1"). Render it as a
// whole-or-fractional percent; non-numeric input passes through verbatim so a
// malformed value never surfaces as "NaN%".
function formatPercent(percent) {
    const pct = Number(percent) * 100;
    if (!Number.isFinite(pct))
        return percent;
    return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(pct)}%`;
}
// `blTrancheIndex` is the 0-based running position of this tranche among the
// side's `BLTranche`-kind lines (NOT `tranche.position`, which is 1-based in the
// seed and would mislabel the lone BL tranche as "BL Tranche 2"). It is ignored
// for every other kind.
function labelFor(kind, blTrancheIndex) {
    if (kind === 'BLTranche') {
        return `${KIND_LABEL[kind] ?? kind} ${String(blTrancheIndex + 1)}`;
    }
    return KIND_LABEL[kind] ?? kind;
}
function humaniseEvent(triggerEvent) {
    // The wire enum already capitalises each token (e.g. `ETA_at_PoD`), so the
    // underscore swap alone yields Launchpad prose ("ETA at PoD").
    return triggerEvent.replace(/_/g, ' ');
}
// Launchpad trigger prose. A positive offset reads "{n} day(s) {before|after}
// {event}". With no day offset, an explicit timing still shows ("Before ETA at
// PoD" / "After ATA at PoD", matching Launchpad); only a timing-less line
// collapses to "On {event}" (e.g. "On Order Signed Date"). `before` iff the
// timing is "Before"; "After" and an unset timing both read "after" on the
// offset form, mirroring the backend `applyOffset` additive default. A
// non-numeric offset degrades to the bare humanised event.
function formatTriggerPhrase(tranche) {
    if (tranche.trigger_event === null)
        return 'No milestone trigger';
    const event = humaniseEvent(tranche.trigger_event);
    const days = tranche.offset_days === null ? 0 : Number(tranche.offset_days);
    if (!Number.isFinite(days))
        return event;
    if (days === 0) {
        if (tranche.trigger_timing === 'Before')
            return `Before ${event}`;
        if (tranche.trigger_timing === 'After')
            return `After ${event}`;
        return `On ${event}`;
    }
    const direction = tranche.trigger_timing === 'Before' ? 'before' : 'after';
    const dayWord = days === 1 ? 'day' : 'days';
    return `${String(days)} ${dayWord} ${direction} ${event}`;
}
// The settlement-method / role phrase. The contract terms write it into
// `notes` ("Advance via TT with SO", "DP via Bank") — preserved verbatim so the
// settlement abbreviations (TT / SO / DP) keep their casing. Falls back to the
// humanised kind so a note-less tranche still reads.
function clauseRole(tranche) {
    if (tranche.notes !== null && tranche.notes.trim() !== '') {
        return tranche.notes.trim();
    }
    return labelFor(tranche.kind, 0);
}
// One contract-terms clause: "{percent} {role}{, trigger phrase}". The trigger
// phrase is appended only when it carries an offset (the "On {event}" form is
// implied by the role on the advance line and reads redundantly inline).
function clauseFor(tranche) {
    const head = `${formatPercent(tranche.percent)} ${clauseRole(tranche)}`;
    const days = tranche.offset_days === null ? 0 : Number(tranche.offset_days);
    if (tranche.trigger_event === null || !Number.isFinite(days) || days === 0) {
        return head;
    }
    return `${head}, ${formatTriggerPhrase(tranche)}`;
}
// The one-line prose summary of the side's payment plan, e.g.
// "10% Advance via TT with SO, 90% DP via Bank, 7 days before ETA at PoD".
// Returns null for an empty side so the renderer omits the line.
function buildContractTermsSummary(side) {
    if (side.tranches.length === 0)
        return null;
    return side.tranches.map(clauseFor).join(', ');
}
//# sourceMappingURL=payment-schedule.js.map